import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowRight, Loader2, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import StatusDot from '@/components/StatusDot';
import { WorkQueueRow } from '@/components/WorkQueueRow';
import api from '@/lib/api';

const FDNY_RESULT_COLORS = {
  'Passed': '#16A34A', 'Failed': '#E5441A', 'Re-inspection Required': '#D97706',
  'Pending': '#031333', 'Scheduled': '#1B4A76',
};

export default function DashboardPage() {
  const { user, effectiveRole } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [propertyCount, setPropertyCount] = useState(0);
  const [openViolations, setOpenViolations] = useState(0);
  const [permitsExpiring, setPermitsExpiring] = useState(0);
  const [cotcoPending, setCotcoPending] = useState(0);
  const [cotcoPipeline, setCotcoPipeline] = useState([]);
  const [cotcoExporting, setCotcoExporting] = useState(false);
  const [complianceActive, setComplianceActive] = useState([]);
  const [dobExpiring, setDobExpiring] = useState([]);
  const [fdnyDashboard, setFdnyDashboard] = useState([]);
  const [acceptedProposals, setAcceptedProposals] = useState([]);
  const [creatingId, setCreatingId] = useState(null);
  const [workQueue, setWorkQueue] = useState([]);

  const isAdmin = effectiveRole === 'Administrator';
  const canCreateProject = effectiveRole === 'Administrator' || effectiveRole === 'Owner';

  useEffect(() => {
    api.get('/dashboard').then(r => {
      setStats(r.data);
      setAcceptedProposals(Array.isArray(r.data?.accepted_unlinked_proposals) ? r.data.accepted_unlinked_proposals : []);
      setLoading(false);
    }).catch(() => setLoading(false));

    const today = new Date();
    const start = today.toISOString().slice(0, 10);
    const futureDate = new Date(today);
    futureDate.setDate(futureDate.getDate() + 30);
    const end = futureDate.toISOString().slice(0, 10);
    api.get(`/calendar/events?start=${start}&end=${end}`).then(r => {
      setUpcomingEvents(Array.isArray(r.data) ? r.data.slice(0, 5) : []);
    }).catch(() => {});

    api.get('/properties').then(r => {
      setPropertyCount(Array.isArray(r.data) ? r.data.length : 0);
    }).catch(() => {});

    api.get('/violations/summary').then(r => {
      setOpenViolations(r.data?.total_open ?? 0);
    }).catch(() => setOpenViolations(0));

    api.get('/dot-permits/summary').then(r => {
      setPermitsExpiring(r.data?.expiring_30 ?? 0);
    }).catch(() => setPermitsExpiring(0));

    api.get('/cotco/summary').then(r => {
      setCotcoPending(r.data?.projects_with_incomplete_cotco ?? 0);
      setCotcoPipeline(Array.isArray(r.data?.pipeline) ? r.data.pipeline : []);
    }).catch(() => { setCotcoPending(0); setCotcoPipeline([]); });

    api.get('/compliance/active-projects').then(r => {
      setComplianceActive(Array.isArray(r.data) ? r.data : []);
    }).catch(() => setComplianceActive([]));

    api.get('/dob-filings/expiring-projects').then(r => {
      setDobExpiring(Array.isArray(r.data) ? r.data : []);
    }).catch(() => setDobExpiring([]));

    api.get('/fdny-inspections/dashboard').then(r => {
      setFdnyDashboard(Array.isArray(r.data) ? r.data : []);
    }).catch(() => setFdnyDashboard([]));

    api.get('/work-queue').then(r => {
      setWorkQueue(Array.isArray(r.data) ? r.data : []);
    }).catch(() => setWorkQueue([]));
  }, []);

  const createProjectFromProposal = async (proposalId) => {
    setCreatingId(proposalId);
    try {
      const r = await api.post(`/proposals/${proposalId}/create-project`);
      toast.success(`Project ${r.data.project_number} created`);
      setAcceptedProposals(prev => (Array.isArray(prev) ? prev : []).filter(p => p.id !== proposalId));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create project');
    }
    setCreatingId(null);
  };

  const exportCotcoPdf = async () => {
    setCotcoExporting(true);
    try {
      const r = await api.get('/cotco/export-pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'CO_TCO_Pipeline_Report.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Error exporting CO/TCO report'); }
    setCotcoExporting(false);
  };

  if (loading) return <div className="animate-pulse text-muted-foreground">Loading dashboard...</div>;

  const stripStats = [
    { title: 'Total Properties', value: propertyCount, to: '/properties' },
    { title: 'Open Violations', value: openViolations, to: '/properties?tab=violations' },
    { title: 'Active Projects', value: stats?.active_projects || 0, to: '/projects' },
    { title: 'Pending Proposals', value: stats?.pending_proposals || 0, to: '/proposals' },
    { title: 'Total Clients', value: stats?.total_clients || 0, to: '/clients' },
    { title: 'Open Tasks', value: stats?.open_tasks || 0, to: '/tasks' },
    { title: 'Permits Expiring (30d)', value: permitsExpiring, to: '/properties?tab=dot-permits' },
    { title: 'CO/TCO Pending', value: cotcoPending, to: '/projects' },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      <div>
        <h1 className="font-semibold text-lg tracking-tight text-foreground">Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Welcome back, {user?.name}</p>
      </div>

      {/* Administrator system overview removed: consolidated into the single stat strip below */}

      {/* TOP ROW: responsive stat strip: 8 cols (md+), 4 cols (xs), 2 cols (<480) */}
      <div className="border border-border rounded bg-card grid grid-cols-2 xs:grid-cols-4 md:grid-cols-8 gap-y-3" style={{ padding: '16px' }} data-testid="dashboard-stats">
        {stripStats.map((s) => (
          <div
            key={s.title}
            onClick={() => navigate(s.to)}
            className="cursor-pointer px-4 border-[#D4DCE8] [&:nth-child(2n)]:border-l xs:[&:nth-child(4n+2)]:border-l xs:[&:nth-child(4n+3)]:border-l xs:[&:nth-child(4n)]:border-l md:[&:nth-child(n+2)]:border-l"
            data-testid={`stat-${s.title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <p className="text-sm"><span style={{ color: '#031333' }}>{s.title}: </span><span style={{ fontFamily: "'DM Mono', monospace", color: '#010A21' }}>{s.value}</span></p>
          </div>
        ))}
      </div>

      {/* Administrator-only shortcut */}
      {isAdmin && (
        <div className="-mt-3" data-testid="admin-shortcut">
          <button
            onClick={() => navigate('/employees')}
            className="text-sm text-[#1B4A76] hover:underline inline-flex items-center gap-1"
            data-testid="admin-employees-link"
          >
            Admin: Manage Employees →
          </button>
        </div>
      )}

      {/* Proposals Awaiting Project Creation: Admin/Owner only, hidden when empty */}
      {canCreateProject && (Array.isArray(acceptedProposals) ? acceptedProposals : []).length > 0 && (
        <Card className="border-border bg-card shadow-none" data-testid="dashboard-awaiting-project-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-semibold" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: '#010A21' }}>
              Proposals Awaiting Project Creation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {(Array.isArray(acceptedProposals) ? acceptedProposals : []).slice(0, 5).map((p) => (
              <div
                key={p.id}
                className="flex items-center justify-between gap-4 py-1.5 border-b border-border last:border-0"
                data-testid={`awaiting-project-${p.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium" style={{ fontFamily: "'DM Mono', monospace", fontSize: '14px', color: '#010A21' }}>{p.proposal_number}</p>
                    {p.accepted_date && (
                      <span className="text-xs text-muted-foreground" style={{ fontFamily: "'DM Mono', monospace" }} data-testid={`awaiting-project-date-${p.id}`}>{p.accepted_date}</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{p.client_name || p.title || p.name || '\u00A0'}</p>
                </div>
                <Button
                  size="sm"
                  disabled={creatingId === p.id}
                  onClick={() => createProjectFromProposal(p.id)}
                  style={{ backgroundColor: '#E5441A', color: '#fff' }}
                  className="hover:opacity-90 shrink-0"
                  data-testid={`create-project-btn-${p.id}`}
                >
                  {creatingId === p.id
                    ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Creating...</>
                    : <>Create Project <ArrowRight className="h-3.5 w-3.5 ml-1" /></>}
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* SECOND ROW: 60/40 - Recent Projects | Upcoming Events */}
      <div className="grid grid-cols-5 gap-4">
        <Card className="col-span-5 lg:col-span-3 border-border bg-card shadow-none cursor-pointer hover:bg-secondary/30 transition-colors" onClick={() => navigate('/projects')}>
          <CardHeader className="pb-3">
            <CardTitle className="font-semibold" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: '#010A21' }}>
              Recent Projects
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {(stats?.recent_projects || []).length === 0 ? (
              <p className="text-xs text-muted-foreground">No projects yet</p>
            ) : (
              (Array.isArray(stats.recent_projects) ? stats.recent_projects : []).map((p) => (
                <div key={p.id} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium font-mono">{p.project_number}</p>
                      {(Array.isArray(dobExpiring) ? dobExpiring : []).includes(p.id) && (
                        <span
                          className="inline-flex items-center rounded-full "
                          style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '12px', fontWeight: 600, padding: '2px 8px', color: '#B45309', backgroundColor: '#FEF3C7' }}
                          data-testid={`dob-badge-${p.id}`}
                        >
                          DOB
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{p.name}</p>
                  </div>
                  <StatusDot status={p.status} />
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card className="col-span-5 lg:col-span-2 border-border bg-card shadow-none cursor-pointer hover:bg-secondary/30 transition-colors" onClick={() => navigate('/calendar')} data-testid="dashboard-calendar-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-semibold" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: '#010A21' }}>
              Upcoming Events
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {upcomingEvents.length === 0 ? (
              <p className="text-xs text-muted-foreground">No upcoming events in the next 30 days</p>
            ) : (
              (Array.isArray(upcomingEvents) ? upcomingEvents : []).map((ev) => (
                <div key={ev.id} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{ev.name}</p>
                    <p className="text-xs text-muted-foreground font-mono">{ev.date}{ev.project_name ? ` · ${ev.project_name}` : ''}</p>
                  </div>
                  {ev.event_type && <span className="text-xs text-muted-foreground shrink-0 ml-2">{ev.event_type}</span>}
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* CO/TCO Pipeline */}
      <Card className="border-border bg-card shadow-none" data-testid="dashboard-cotco-pipeline">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="font-semibold" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: '#010A21' }}>
              CO/TCO Pipeline
            </CardTitle>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-muted-foreground hover:text-foreground" onClick={exportCotcoPdf} disabled={cotcoExporting} title="Export CO/TCO Pipeline Report" data-testid="export-cotco-pdf-btn">
              <FileDown className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {(Array.isArray(cotcoPipeline) ? cotcoPipeline : []).length === 0 ? (
            <p className="text-xs text-muted-foreground" data-testid="cotco-pipeline-empty">No active CO/TCO checklists.</p>
          ) : (
            (Array.isArray(cotcoPipeline) ? cotcoPipeline : []).map((pr) => (
              <div
                key={pr.id}
                className="flex items-center justify-between gap-4 py-1.5 border-b border-border last:border-0 cursor-pointer hover:bg-secondary/20"
                onClick={() => navigate(`/projects?open=${pr.id}`)}
                data-testid={`cotco-pipeline-${pr.id}`}
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{pr.name}</p>
                  {(Array.isArray(complianceActive) ? complianceActive : []).includes(pr.id) && (
                    <span
                      className="shrink-0 inline-flex items-center rounded-full "
                      style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '12px', fontWeight: 600, padding: '2px 8px', color: '#B45309', backgroundColor: '#FEF3C7' }}
                      data-testid={`compliance-badge-${pr.id}`}
                    >
                      Compliance
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0 w-40">
                  <div className="flex-1 h-1.5 rounded bg-secondary overflow-hidden">
                    <div className="h-full rounded" style={{ width: `${(pr.completed / (pr.total || 10)) * 100}%`, backgroundColor: '#16A34A' }} />
                  </div>
                  <span className="text-xs text-muted-foreground font-mono">{pr.completed}/{pr.total || 10}</span>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* FDNY Inspections */}
      <Card className="border-border bg-card shadow-none" data-testid="dashboard-fdny-inspections">
        <CardHeader className="pb-3">
          <CardTitle className="font-semibold" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: '#010A21' }}>
            FDNY Inspections
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {(Array.isArray(fdnyDashboard) ? fdnyDashboard : []).length === 0 ? (
            <p className="text-xs text-muted-foreground" data-testid="fdny-inspections-empty">No upcoming FDNY inspections.</p>
          ) : (
            (Array.isArray(fdnyDashboard) ? fdnyDashboard : []).map((insp) => (
              <div
                key={insp.id}
                className="flex items-center justify-between gap-4 py-1.5 border-b border-border last:border-0 cursor-pointer hover:bg-secondary/20"
                onClick={() => navigate(`/properties?open=${insp.property_id}`)}
                data-testid={`fdny-dashboard-${insp.id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{insp.property_display_name || insp.property_address || 'Property'}</p>
                  <p className="text-xs text-muted-foreground truncate">{insp.inspection_type || 'FDNY Inspection'}</p>
                </div>
                <span className="text-xs text-muted-foreground shrink-0" style={{ fontFamily: "'DM Mono', monospace" }}>{insp.inspection_date || '\u00A0'}</span>
                <span className="shrink-0 inline-flex items-center gap-1.5">
                  <span className="status-dot" style={{ backgroundColor: FDNY_RESULT_COLORS[insp.result] || '#71717A' }} />
                  <span className="text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>{insp.result || 'Pending'}</span>
                </span>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Work Queue card (moved to bottom) */}
      <Card className="border-border bg-card shadow-none" data-testid="dashboard-work-queue-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-semibold" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: '#010A21' }}>
            Work Queue
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-0">
          {(() => {
            const top = (Array.isArray(workQueue) ? workQueue : []).filter(i => i.urgency === 'critical' || i.urgency === 'warning').slice(0, 5);
            if (top.length === 0) {
              return <p style={{ fontSize: '14px', color: '#16A34A' }} data-testid="wq-dashboard-empty">All clear.</p>;
            }
            return (
              <>
                <div>
                  {top.map(i => <WorkQueueRow key={i.id} item={i} onGo={(u) => navigate(u)} />)}
                </div>
                <div className="pt-3">
                  <button onClick={() => navigate('/work-queue')} className="hover:underline" style={{ fontSize: '14px', color: '#1B4A76' }} data-testid="wq-view-all-link">
                    View all →
                  </button>
                </div>
              </>
            );
          })()}
        </CardContent>
      </Card>
    </div>
  );
}
